import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Text,
} from '@chakra-ui/react';
import { useActiveContext } from '@texas/hooks/useActiveContext';
import { ReactSelectOption } from '@texas/types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { setValueAsDecimalExtender } from '../article/variant/sales/shared';
import { TexasFormLabel } from '../shared/form/TexasFormLabel';
import { TexasSelect } from '../shared/form/TexasSelect';
import { PreviewPane } from './PreviewPanel';
import { formatRange } from './utils';
import { VdcFormValues } from './useVdcConfiguration';
import { VdcConfigContext } from './VdcPricing';
import { PreviewTemplate } from './PreviewTemplate';

export function ConfigSection() {
  const { t } = useTranslation();
  const {
    productGroups,
    loadingProductGroups,
    branches,
    loadingBranches,
    templateRangeTiers,
    loadingTemplateRangeTiers,
    templates,
    loadingTemplates,
    treatments,
    loadingTreatments,
    addValues,
    configuredBranches,
    updateBranches,
  } = useActiveContext(VdcConfigContext);
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<VdcFormValues>();

  const form = watch();

  const submit = (data: VdcFormValues) => {
    addValues(data);
  };

  const compositionGroupId = watch('compositionGroupId');
  return (
    <Box maxH="calc(100dvh - 88px)" overflowY="auto" pos="relative" pr={6}>
      <form style={{ minHeight: '100%' }} onSubmit={handleSubmit(submit)}>
        <Flex flexDir="column" gap={12} w="440px">
          <Step nr={1}>
            <Flex flexDir="column" gap={2}>
              <FormControl isInvalid={!!errors.productGroupId}>
                <TexasFormLabel>{t('general.productGroup')}</TexasFormLabel>
                <Controller
                  name="productGroupId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TexasSelect
                      {...field}
                      isLoading={loadingProductGroups}
                      isClearable={true}
                      value={{
                        label:
                          productGroups?.find((p) => p.id === field.value)
                            ?.value ?? '',
                        value: field.value,
                      }}
                      onChange={(e) => field.onChange(e?.value)}
                      options={
                        productGroups?.map<ReactSelectOption>((x) => ({
                          value: x.id,
                          label: x.value,
                        })) ?? []
                      }
                    />
                  )}
                />
                <FormErrorMessage>
                  {errors.productGroupId?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl>
                <TexasFormLabel>{t('general.branches')}</TexasFormLabel>
                <TexasSelect
                  isMulti={true}
                  closeMenuOnSelect={false}
                  isLoading={loadingBranches}
                  value={configuredBranches.map<ReactSelectOption>((x) => {
                    const branch = branches.find((p) => p.id === x);
                    return {
                      label: branch ? branch.name : '',
                      value: x,
                    };
                  })}
                  onChange={(e) => updateBranches(e.map((x) => x.value))}
                  options={branches.map<ReactSelectOption>((x) => ({
                    value: x.id,
                    label: x.name,
                  }))}
                />
              </FormControl>
            </Flex>
          </Step>
          <Step nr={2}>
            <Flex flexDir="column" gap={2}>
              <FormControl isInvalid={!!errors.width}>
                <TexasFormLabel>{t('vdc.labelWidth')}</TexasFormLabel>
                <Input
                  {...register('width', {
                    required: true,
                    setValueAs: (value) =>
                      setValueAsDecimalExtender(value, false),
                  })}
                  variant="texas"
                />
                <FormErrorMessage>{errors.width?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.length}>
                <TexasFormLabel>{t('vdc.labelLength')}</TexasFormLabel>
                <Input
                  {...register('length', {
                    required: true,
                    setValueAs: (value) =>
                      setValueAsDecimalExtender(value, false),
                  })}
                  variant="texas"
                />
                <FormErrorMessage>{errors.length?.message}</FormErrorMessage>
              </FormControl>
            </Flex>
          </Step>
          <Step nr={3}>
            <FormControl isInvalid={!!errors.tierRangeId}>
              <TexasFormLabel>
                {t('vdc.estimatedAnnualQuantity')}
              </TexasFormLabel>
              <Controller
                name="tierRangeId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => {
                  const selectedRange = templateRangeTiers?.find(
                    (p) => p.id === field.value,
                  );
                  return (
                    <TexasSelect
                      {...field}
                      isLoading={loadingTemplateRangeTiers}
                      isClearable={true}
                      value={{
                        label: selectedRange
                          ? formatRange(
                              selectedRange.startRange,
                              selectedRange.endRange,
                            )
                          : '',
                        value: field.value,
                      }}
                      onChange={(e) => field.onChange(e?.value)}
                      options={
                        templateRangeTiers?.map<ReactSelectOption>((x) => ({
                          value: x.id,
                          label: formatRange(x.startRange, x.endRange),
                        })) ?? []
                      }
                    />
                  );
                }}
              />
              <FormErrorMessage>{errors.tierRangeId?.message}</FormErrorMessage>
            </FormControl>
          </Step>
          <Step nr={4}>
            <FormControl isInvalid={!!errors.compositionGroupId}>
              <TexasFormLabel>{t('vdc.vdcTemplate')}</TexasFormLabel>
              <Controller
                name="compositionGroupId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => {
                  const template = templates?.find((p) => p.id === field.value);
                  return (
                    <TexasSelect
                      {...field}
                      isLoading={loadingTemplates}
                      isClearable={true}
                      value={{
                        label: template
                          ? !template.note || template.note.length == 0
                            ? t('compositionGroup.option.optionNr', {
                                nr: template.version,
                              })
                            : template.note
                          : '',
                        value: field.value,
                      }}
                      onChange={(e) => field.onChange(e?.value)}
                      options={
                        templates?.map<ReactSelectOption>((x) => ({
                          value: x.id,
                          label:
                            !x.note || x.note.length == 0
                              ? t('compositionGroup.option.optionNr', {
                                  nr: x.version,
                                })
                              : x.note,
                        })) ?? []
                      }
                    />
                  );
                }}
              />
              <FormErrorMessage>
                {errors.compositionGroupId?.message}
              </FormErrorMessage>
            </FormControl>
          </Step>
          <Step nr={5}>
            <FormControl>
              <TexasFormLabel>{t('configuration.treatments')}</TexasFormLabel>
              <Controller
                name="treatmentIds"
                control={control}
                render={({ field }) => (
                  <TexasSelect
                    {...field}
                    isMulti={true}
                    isLoading={loadingTreatments}
                    isClearable={true}
                    value={field.value?.map<ReactSelectOption>((x) => ({
                      label: treatments?.find((p) => p.id === x)?.name ?? '',
                      value: x,
                    }))}
                    onChange={(e) => field.onChange(e.map((x) => x.value))}
                    options={
                      treatments?.map<ReactSelectOption>((x) => ({
                        value: x.id,
                        label: x.name,
                      })) ?? []
                    }
                    closeMenuOnSelect={false}
                  />
                )}
              />
            </FormControl>
          </Step>
          {compositionGroupId && (
            <PreviewTemplate compositionGroupId={compositionGroupId} />
          )}
        </Flex>
      </form>
      <PreviewPane
        formValues={form}
        selectedBranches={configuredBranches}
        disabled={!isValid || isSubmitting || configuredBranches.length === 0}
        onAdd={() => {
          handleSubmit(submit)();
        }}
      />
    </Box>
  );
}

function Step({ nr, children }: { nr: number; children: React.ReactNode }) {
  return (
    <Flex gap={4} flexGrow={1} flex="1">
      <Flex
        boxSize={8}
        minW={8}
        bg="texas.bg.800"
        borderRadius="md"
        alignItems="center"
        justifyContent="center"
        _light={{
          bg: 'gray.100',
        }}
      >
        <Text>{nr}</Text>
      </Flex>
      <Box flexGrow={1}>{children}</Box>
    </Flex>
  );
}
